import type { SessionResponse } from '@schemas/session'
import { type WorkspaceMembersResponse, WorkspaceRoleEnum } from '@schemas/workspace'
import { m } from 'motion/react'
import { useCallback } from 'react'
import { Link } from 'react-router'
import { api } from '../../api.ts'
import { adminEmails, namedAdmins } from '../../lib/admins.ts'
import { fadeSlideUp } from '../../lib/motion.ts'
import { Badge } from '../badge.tsx'
import { Card, CardBody } from '../card.tsx'
import { useSectionQuery } from '../command-center/use-section-query.ts'

export interface AccessDeniedProps {
  session: SessionResponse
  /** What the member tried to reach, as the headline names it — "Campaign settings", "Activity". */
  surface: string
  /** What specifically belongs to an admin here, for the second sentence. */
  belongsToAdmin: string
}

// RAL-121 board D, the access-denied refusal: not a 403 and not the user's fault. It names what
// happened, whose decision it was, and what this person can do instead — including the workspace
// where they are an admin themselves, so the screen is never a dead end.
//
// Part 2 names the admins, per rule 6: the roster read is member-readable, so this screen can
// say "jules@… or marco@… can change them" rather than "an admin". The request is best-effort —
// a refusal must still render if it fails, so an unavailable roster falls back to "An admin".
export function AccessDenied({ session, surface, belongsToAdmin }: AccessDeniedProps) {
  const workspaceName = session.activeWorkspace.name || 'this workspace'
  const roster = useSectionQuery(
    useCallback(() => api.get<WorkspaceMembersResponse>('/api/workspace/members'), []),
    'team roster'
  )
  const approvers = namedAdmins(adminEmails(roster.data?.members ?? []))
  const ownWorkspace = session.workspaces.find(
    (workspace) => workspace.role === WorkspaceRoleEnum.enum.admin && workspace.id !== session.activeWorkspace.id
  )
  return (
    <m.div variants={fadeSlideUp} initial="hidden" animate="visible" className="mx-auto max-w-xl">
      <Card>
        <CardBody className="flex flex-col gap-5 px-8 py-9">
          <Badge variant="neutral">Admin only</Badge>

          <h1 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink-primary">
            {surface} are an admin's in {workspaceName}
          </h1>

          <p className="max-w-prose text-sm leading-relaxed text-ink-soft">
            Your access here is analytics — you can read everything Rally has published and learned, but{' '}
            {belongsToAdmin} belong to an admin.{' '}
            {approvers ? `${approvers} can change them.` : `An admin in ${workspaceName} can change them.`}
          </p>

          <div className="flex flex-wrap items-center gap-5 border-t border-border-hairline pt-4">
            <Link to="/published" className="font-mono text-[10px] uppercase tracking-[0.11em] text-signal-oxide">
              Go to published →
            </Link>
            <Link to="/intelligence" className="font-mono text-[10px] uppercase tracking-[0.11em] text-signal-oxide">
              What Rally reads →
            </Link>
          </div>

          {ownWorkspace ? (
            <p className="font-mono text-[11px] text-ink-secondary">
              In your own workspace, {ownWorkspace.name}, you're an admin.
            </p>
          ) : null}
        </CardBody>
      </Card>
    </m.div>
  )
}
